import { useEffect, useState } from "react";

import { toast } from "react-toastify";
import Button from "@Components/Button";
import Loading from "@Components/Loading";
import { TitleIssue } from "@Types/title_issues";
import { useWisconsinLienCheck } from "@Hooks/useTitleIssues";

import styles from "./DmvInfo.module.scss";

type Phase = "instructions" | "waiting" | "entry" | "done";

function getWiLienUrl(vin: string, titleIssueId: string): string {
  const url = process.env.REACT_APP_HTTP_URL ?? "";
  let env = "dev";
  if (url.includes("localhost")) env = "local";
  else if (url.includes("bhm9qyb6")) env = "dev";
  else if (url.includes("5ac66gkm")) env = "stable";
  else if (url.includes("8xnd8u5u")) env = "production";

  const params = new URLSearchParams({ whoami: "linqp1", vin, entityId: titleIssueId, env });
  return `https://trust.dot.state.wi.us/linq/linqservlet?${params.toString()}`;
}

export default function WisconsinLienCheckDialog({
  titleIssue,
  open,
  setOpen,
}: {
  titleIssue: TitleIssue;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const vin = titleIssue?.data?.deal?.data?.info?.vehicle?.VIN ?? "";
  const titleIssueId = titleIssue?._id ?? "";

  const [phase, setPhase] = useState<Phase>("instructions");
  const [hasLien, setHasLien] = useState(false);
  const [lienholderName, setLienholderName] = useState("");
  const [lienholderAddress, setLienholderAddress] = useState("");
  const [receivedDate, setReceivedDate] = useState("");

  const { saveResult, saving, saved, saveError, reset } = useWisconsinLienCheck(titleIssueId);

  useEffect(() => {
    if (saved) {
      setPhase("done");
      toast.success("WI lien result saved successfully.", { position: "top-center" });
    }
  }, [saved]);

  useEffect(() => {
    if (saveError) {
      toast.error(saveError.message ?? "Failed to save lien result.", { position: "top-center" });
    }
  }, [saveError]);

  const handleClose = () => {
    setOpen(false);
    reset();
    setPhase("instructions");
  };

  const handleOpenPopup = () => {
    window.open(
      getWiLienUrl(vin, titleIssueId),
      "wi-lien-lookup",
      "width=1100,height=820,resizable=yes,scrollbars=yes"
    );
    setPhase("waiting");
  };

  const handleSave = () => {
    saveResult({
      titleIssueId,
      hasLien,
      lienholderName: lienholderName.trim() || null,
      lienholderAddress: lienholderAddress.trim() || null,
      receivedDate: receivedDate.trim() || null,
    });
  };

  if (!open) return null;

  return (
    <dialog
      className={styles.closeDialogContainer}
      style={{ width: "40%" }}
      open={true}
    >
      <div className={styles.closeDialogContent}>
        <div className={styles.header}>Wisconsin Lienholder Lookup</div>

        {/* ── Instructions ── */}
        {phase === "instructions" && (
          <div>
            <p>
              This will open the <strong>WI DOT Lien Inquiry</strong> page in a new window
              with the VIN pre-filled.
            </p>
            <p style={{ fontFamily: "monospace", fontSize: 13 }}>VIN: {vin || "—"}</p>
            <hr style={{ margin: "12px 0" }} />
            <p><strong>Steps:</strong></p>
            <ol style={{ paddingLeft: 20, lineHeight: 1.8 }}>
              <li>Click <strong>Open WI DOT</strong> below</li>
              <li>Solve the reCAPTCHA in the popup</li>
              <li>Click <strong>Next</strong></li>
              <li>Note the lien result shown on that page</li>
              <li>Come back here and enter what you saw</li>
            </ol>
          </div>
        )}

        {/* ── Waiting ── */}
        {phase === "waiting" && (
          <div>
            <p>The WI DOT page is open in a popup. After reading the result, click below.</p>
          </div>
        )}

        {/* ── Entry ── */}
        {phase === "entry" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <p style={{ margin: 0 }}>Enter the lien information from the WI DOT result page.</p>

            <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={hasLien}
                onChange={(e) => setHasLien(e.target.checked)}
                style={{ width: 18, height: 18 }}
              />
              <span style={{ fontWeight: 600, color: hasLien ? "#d32f2f" : "rgb(80,165,56)" }}>
                {hasLien ? "Lien Present" : "No Lien / Clear Title"}
              </span>
            </label>

            {hasLien && (
              <>
                <div>
                  <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>
                    Lienholder Name
                  </label>
                  <input
                    type="text"
                    value={lienholderName}
                    onChange={(e) => setLienholderName(e.target.value)}
                    style={inputStyle}
                    placeholder="e.g. First National Bank"
                    autoFocus
                  />
                </div>
                <div>
                  <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>
                    Lienholder Address
                  </label>
                  <input
                    type="text"
                    value={lienholderAddress}
                    onChange={(e) => setLienholderAddress(e.target.value)}
                    style={inputStyle}
                    placeholder="e.g. 123 Main St, Milwaukee WI 53201"
                  />
                </div>
                <div>
                  <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>
                    Received Date
                  </label>
                  <input
                    type="date"
                    value={receivedDate}
                    onChange={(e) => setReceivedDate(e.target.value)}
                    style={inputStyle}
                  />
                </div>
              </>
            )}

            {saving && (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Loading />
              </div>
            )}
          </div>
        )}

        {/* ── Done ── */}
        {phase === "done" && (
          <p>
            <strong style={{ color: "rgb(80,165,56)" }}>Saved!</strong> The lien result has been
            recorded.
          </p>
        )}
      </div>

      <div className={styles.closeDialogButtons}>
        <Button className={styles.closeDialogButton} onClick={handleClose} variant="pink">
          {phase === "done" ? "Close" : "Cancel"}
        </Button>

        {phase === "instructions" && (
          <Button className={styles.closeDialogButton} onClick={handleOpenPopup} variant="blue">
            Open WI DOT
          </Button>
        )}

        {phase === "waiting" && (
          <Button
            className={styles.closeDialogButton}
            onClick={() => setPhase("entry")}
            variant="blue"
          >
            Enter Manually
          </Button>
        )}

        {phase === "entry" && !saving && (
          <Button className={styles.closeDialogButton} onClick={handleSave} variant="blue">
            Save Result
          </Button>
        )}
      </div>
    </dialog>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid #ccc",
  fontSize: 14,
  boxSizing: "border-box",
};
